import type { AccountSummary, Chat, ConversationType } from '../types';

export interface ChatActionCapabilities {
  canSend: boolean;
  canChangeConsent: boolean;
  canAutomode: boolean;
  canAssignAgent: boolean;
  canCall: boolean;
  canShowContactMemory: boolean;
  canShowContactDetails: boolean;
  canChangeRelationship: boolean;
  canFavorite: boolean;
  canDelete: boolean;
}

export function conversationTypeLabel(type: ConversationType | undefined): string {
  switch (type) {
    case 'direct':
      return 'Chat';
    case 'group':
      return 'Groep';
    case 'community':
      return 'Community';
    case 'channel':
      return 'Kanaal';
    case 'status':
      return 'Status';
    case 'broadcast':
      return 'Broadcast';
    case 'unknown':
      return 'Onbekend';
    default:
      return 'Chat';
  }
}

export function getChatCapabilities(chat: { conversationType?: ConversationType | undefined }): ChatActionCapabilities {
  switch (chat.conversationType) {
    case 'direct':
      return {
        canSend: true,
        canChangeConsent: true,
        canAutomode: true,
        canAssignAgent: true,
        canCall: true,
        canShowContactMemory: true,
        canShowContactDetails: true,
        canChangeRelationship: true,
        canFavorite: true,
        canDelete: true,
      };
    case 'group':
    case 'community':
      return {
        canSend: true,
        canChangeConsent: false,
        canAutomode: false,
        canAssignAgent: false,
        canCall: false,
        canShowContactMemory: false,
        canShowContactDetails: false,
        canChangeRelationship: false,
        canFavorite: true,
        canDelete: true,
      };
    case 'channel':
    case 'status':
    case 'broadcast':
      return {
        canSend: false,
        canChangeConsent: false,
        canAutomode: false,
        canAssignAgent: false,
        canCall: false,
        canShowContactMemory: false,
        canShowContactDetails: false,
        canChangeRelationship: false,
        canFavorite: true,
        canDelete: true,
      };
    case 'unknown':
    default:
      return {
        canSend: false,
        canChangeConsent: false,
        canAutomode: false,
        canAssignAgent: false,
        canCall: false,
        canShowContactMemory: false,
        canShowContactDetails: false,
        canChangeRelationship: false,
        canFavorite: true,
        canDelete: true,
      };
  }
}

export function isConversationTypeSupported(
  account: AccountSummary | null | undefined,
  conversationType: ConversationType | undefined,
): boolean {
  if (!account) return false;
  return account.capabilities.some(
    (capability) => capability.type === conversationType && capability.supported,
  );
}

export function getUnsupportedReason(
  account: AccountSummary | null | undefined,
  conversationType: ConversationType | undefined,
): string | undefined {
  if (!account) return 'Geen actief WhatsApp-account.';
  const capability = account.capabilities.find((c) => c.type === conversationType);
  if (capability?.supported) return undefined;
  return capability?.reason ?? 'Deze gesprekstypes worden niet ondersteund door het actieve account.';
}

export function getEffectiveConversationType(chat: Chat): ConversationType {
  return chat.conversationType ?? (chat.phone ? 'direct' : 'unknown');
}
